import { useCallback } from 'react';
import type { TabState } from '../types';

interface TabBarProps {
  tabs: TabState[];
  activeTabId: string;
  onSelectTab: (tabId: string) => void;
  onCloseTab: (tabId: string) => void;
  onNewTab: () => void;
}

export default function TabBar({
  tabs,
  activeTabId,
  onSelectTab,
  onCloseTab,
  onNewTab,
}: TabBarProps) {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const activeIdx = tabs.findIndex((t) => t.id === activeTabId);
      if (e.key === 'ArrowRight' && activeIdx < tabs.length - 1) {
        e.preventDefault();
        onSelectTab(tabs[activeIdx + 1].id);
      } else if (e.key === 'ArrowLeft' && activeIdx > 0) {
        e.preventDefault();
        onSelectTab(tabs[activeIdx - 1].id);
      }
    },
    [tabs, activeTabId, onSelectTab],
  );

  return (
    <div
      className="tab-bar"
      role="tablist"
      aria-label="Query tabs"
      onKeyDown={handleKeyDown}
    >
      {/* Action buttons — left of tabs */}
      <div className="tab-bar-actions">
        <button
          className="tab-bar-action"
          onClick={onNewTab}
          title="New Chat (⌘N)"
          aria-label="New chat"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>New</span>
        </button>

      </div>
      <div className="tab-bar-scroll">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`tab-item${tab.id === activeTabId ? ' tab-item--active' : ''}`}
            role="tab"
            aria-selected={tab.id === activeTabId}
            onClick={() => onSelectTab(tab.id)}
            title={tab.label}
          >
            {tab.isStreaming && (
              <span
                className="tab-item-streaming"
                aria-label="Streaming in progress"
              />
            )}
            <span className="tab-item-label">{tab.label}</span>
            {(tabs.length > 1 || tab.label !== 'New Query') && (
              <button
                className="tab-item-close"
                onClick={(e) => {
                  e.stopPropagation();
                  onCloseTab(tab.id);
                }}
                aria-label={`Close ${tab.label}`}
                tabIndex={-1}
                title="Close tab"
              >
                ×
              </button>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
